import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { getStorageKey, normalizeThreadDates } from "@/features/chat/hooks/utils";
import type { ChatThread, ChatThreadWithMessages } from "@/features/chat/services/chatService";
import { type CreateChatThreadParams, chatService } from "@/features/chat/services/chatService";

/**
 * Load thread list scoped to the given workspaceId.
 * Refetches on mount and window focus.
 */
export function useChatThreadList(workspaceId: string | null | undefined) {
	const queryKey = ["chatThreads", workspaceId ?? null] as const;

	const { data: threadsData } = useQuery({
		queryKey,
		queryFn: () => chatService.listThreads({ workspaceId: workspaceId ?? undefined }),
		refetchOnMount: true,
		refetchOnWindowFocus: true,
		staleTime: 0,
	});

	const threads: ChatThread[] = Array.isArray(threadsData) ? threadsData : [];
	return { threads };
}

/**
 * Manages selected thread id, persisted to localStorage keyed by workspaceId.
 */
export function useChatThreadSelection(workspaceId: string | null | undefined, _threads: ChatThread[]) {
	const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);

	useEffect(() => {
		if (typeof window === "undefined") return;
		const stored = localStorage.getItem(getStorageKey(workspaceId));
		setSelectedThreadId(stored || null);
	}, [workspaceId]);

	const handleSelectThread = useCallback(
		(threadId: string) => {
			setSelectedThreadId(threadId);
			if (typeof window !== "undefined") {
				localStorage.setItem(getStorageKey(workspaceId), threadId);
			}
		},
		[workspaceId],
	);

	return { selectedThreadId, setSelectedThreadId, handleSelectThread };
}

/**
 * Create thread mutation. On success: adds thread to list cache, seeds empty messages cache,
 * updates selection and localStorage.
 */
export function useChatCreateThread(
	workspaceId: string | null | undefined,
	setSelectedThreadId: (id: string | null) => void,
) {
	const queryClient = useQueryClient();
	const queryKey = ["chatThreads", workspaceId ?? null] as const;

	return useMutation({
		mutationFn: (params: CreateChatThreadParams) => chatService.createThread(params),
		onSuccess: (newThread) => {
			const normalized = normalizeThreadDates(newThread);

			queryClient.setQueryData<ChatThread[]>(queryKey, (old) => {
				const list = Array.isArray(old) ? old : [];
				if (list.some((t) => t.id === newThread.id)) return list;
				return [...list, normalized];
			});

			const emptyThread: ChatThreadWithMessages = { ...normalized, messages: [] };
			queryClient.setQueryData(["chatThread", newThread.id], emptyThread);

			setSelectedThreadId(newThread.id);
			if (typeof window !== "undefined") {
				localStorage.setItem(getStorageKey(workspaceId), newThread.id);
			}
		},
	});
}

/**
 * Delete thread mutation. On success: invalidates list, removes thread cache,
 * updates selection if the deleted thread was selected.
 */
export function useChatDeleteThread(
	workspaceId: string | null | undefined,
	selectedThreadId: string | null,
	threads: ChatThread[],
	setSelectedThreadId: (id: string | null) => void,
) {
	const queryClient = useQueryClient();
	const queryKey = ["chatThreads", workspaceId ?? null] as const;

	return useMutation({
		mutationFn: (threadId: string) => chatService.deleteThread(threadId),
		onSuccess: (_data, deletedId) => {
			queryClient.invalidateQueries({ queryKey });
			queryClient.removeQueries({ queryKey: ["chatThread", deletedId] });
			if (selectedThreadId === deletedId) {
				const remaining = threads.filter((t) => t.id !== deletedId);
				const next = remaining.length > 0 ? remaining[0].id : null;
				setSelectedThreadId(next);
				if (typeof window !== "undefined") {
					const key = getStorageKey(workspaceId);
					if (next) localStorage.setItem(key, next);
					else localStorage.removeItem(key);
				}
			}
		},
	});
}
